import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import { testimonialService } from '../../services/testimonialService.js';
import { MessageSquareQuote, Plus, Pencil, ArrowLeft } from "lucide-react";

const ViewTestimonial = () => {
  const { testimonialId } = useParams();
  const [testimonial, setTestimonial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTestimonial();
  }, [testimonialId]);

  const fetchTestimonial = async () => {
    try {
      const response = await testimonialService.getTestimonial(testimonialId);
      setTestimonial(response.data.data);
    } catch (err) {
      Alert.error('Failed to load testimonial details');
      console.error(err);
      navigate('/testimonials');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating) => {
    return '⭐'.repeat(rating);
  };

  const getImageSrc = () => {
    if (testimonial && testimonial.image_base64) {
      return `data:image/jpeg;base64,${testimonial.image_base64}`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-3 sm:mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading testimonial details...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!testimonial) {
    return null;
  }

  const imageSrc = getImageSrc();

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                        <MessageSquareQuote className="w-6 h-6 text-buttonBg" />
                        Testimonials
                      </h1>
                    <p className="text-sm sm:text-base text-gray-600">View complete testimonial information</p>
                  </div>
                   <div className='w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3'>
                    <button
                        onClick={() => navigate(`/testimonials/edit/${testimonialId}`)}
                        className="
                          relative overflow-hidden
                          group flex items-center justify-center gap-2
                          px-3 sm:px-5 py-2.5 sm:py-2
                          text-xs sm:text-sm
                          rounded-lg
                          border border-buttonBg
                          bg-white text-buttonBg
                          font-medium whitespace-nowrap

                          transition-colors duration-300 ease-out
                          hover:text-white
                        "
                      >
                        {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                        <span
                          className="
                            absolute inset-0
                            bg-buttonBg
                            scale-x-0
                            group-hover:scale-x-100
                            origin-center
                            transition-transform duration-300 ease-out
                            z-0
                          "
                        />
              {/* ICON */}
                            <Pencil
                              className="
                                w-4 h-4 relative z-10
                                transition-transform duration-300 ease-out
                                group-hover:scale-90
                              "
                            />
                        <span className="relative z-10">Edit Testimonials</span>
                      </button>
                      <button
                        onClick={() => navigate('/testimonials')}
                        className="
                              relative overflow-hidden
                              group flex items-center justify-center gap-2
                              px-3 sm:px-3 py-2.5 sm:py-2
                              text-xs sm:text-sm
                              rounded-lg
                              border border-buttonBg
                              bg-buttonBg text-buttonTxt
                              font-medium whitespace-nowrap

                              transition-colors duration-300 ease-out
                              hover:bg-transparent hover:text-buttonBg
                            "
                          >
                            {/* COLLAPSING BACKGROUND */}
                            <span
                              className="
                                absolute inset-0
                                bg-buttonBg
                                scale-x-100
                                group-hover:scale-x-0
                                origin-center
                                transition-transform duration-300 ease-out
                                z-0
                              "
                            />

                            {/* ICON */}
                            <ArrowLeft
                              className="
                                w-4 h-4 relative z-10
                                transition-transform duration-300 ease-out
                                group-hover:scale-90
                              "
                            />

                            <span className="relative z-10">Back to Testimonials List</span>
                      </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {/* Header Section */}
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 sm:p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-sm bg-white flex items-center justify-center flex-shrink-0 overflow-hidden border-2 border-gray">
                        {imageSrc && !imageError ? (
                          <img 
                            src={imageSrc} 
                            alt={testimonial.full_name}
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                          />
                        ) : (
                          <span className="text-4xl sm:text-5xl">👤</span>
                        )}
                      </div>
                      <div className="flex-1 text-center sm:text-left">
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">{testimonial.full_name}</h2>
                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-4">
                          <p className="text-orange-100 text-base sm:text-lg">{testimonial.location}</p>
                          <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${
                            testimonial.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {testimonial.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    {/* Customer Information */}
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                        Customer Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-base font-medium text-gray-600 mb-2">Testimonial ID</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-lg bg-gray-50 p-4 py-3">{testimonial.id}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-base font-medium text-gray-600 mb-2">Full Name</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-lg bg-gray-50 p-4 py-3">{testimonial.full_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-base font-medium text-gray-600 mb-2">Location</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-lg bg-gray-50 p-4 py-3">{testimonial.location}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-base font-medium text-gray-600 mb-2">Status</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-lg bg-gray-50 p-4 py-3">
                            {testimonial.is_active ? 'Active' : 'Inactive'}
                          </p>
                        </div>
                        {imageSrc && !imageError && (
                          <div className="sm:col-span-2">
                            <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Customer Image</label>
                            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-lg overflow-hidden border-2 border-gray-200">
                              <img 
                                src={imageSrc} 
                                alt={testimonial.full_name}
                                className="w-full h-full object-cover"
                                onError={() => setImageError(true)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Testimonial Content */}
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                        Testimonial Content
                      </h3>
                      <div className="space-y-4 sm:space-y-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Rating</label>
                          <div className="flex items-center gap-2">
                            <span className="text-xl sm:text-2xl text-yellow-500">{renderStars(testimonial.rating)}</span>
                            <span className="text-sm sm:text-base text-gray-700 font-medium">({testimonial.rating}/5)</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Testimonial Text</label>
                          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                            <p className="text-sm sm:text-base text-gray-900 leading-relaxed italic">
                              "{testimonial.testimonial_text}"
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Created By</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-lg bg-gray-50 p-4 py-3">{testimonial.created_by || 'System'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Created At</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium break-words border rounded-lg bg-gray-50 p-4 py-3">{formatDate(testimonial.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Last Updated By</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium border rounded-lg bg-gray-50 p-4 py-3">{testimonial.updated_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Last Updated At</label>
                          <p className="text-sm sm:text-base text-gray-900 font-medium break-words border rounded-lg bg-gray-50 p-4 py-3">{formatDate(testimonial.updated_at)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                        Additional Information
                      </h3>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <span className="text-xl sm:text-2xl flex-shrink-0">ℹ️</span>
                          <div>
                            <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">About Testimonials</h4>
                            <p className="text-xs sm:text-sm text-gray-700">
                              Customer testimonials help build trust and credibility. Active testimonials are displayed
                              on the public-facing website to showcase customer satisfaction and experiences.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewTestimonial;